import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getUserServices } from "../services/goHireService";
import { UserServicesResponse } from "../types/userService.types";
import { Service } from "../../types/service.type";
import { ServiceTypeEnum } from "../../enum/service.enum";

const SERVICES_PAGE_LIMIT = 10;

interface UseUserServicesParams {
  userId: number;
  limit?: number;
}

export const useUserServicesInfinite = ({ userId, limit = SERVICES_PAGE_LIMIT }: UseUserServicesParams) => {
  return useInfiniteQuery<UserServicesResponse, Error, Service[]>({
    queryKey: [...GO_SERVICE_QUERY_KEYS.userServices(userId), { limit: limit }],
    queryFn: ({ pageParam }) => {
      const currentPage = (pageParam as number | undefined) ?? 0;
      return getUserServices(userId, limit, currentPage * limit);
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.meta.totalItems ? allPages.length : undefined;
    },
    select: (data) => data.pages.flatMap((page) => page.items),
    initialPageParam: 0,
    enabled: !!userId,
  });
};

interface UseUserServicesPaginatedParams {
  userId: number;
  limit?: number;
  page?: number;
  type?: ServiceTypeEnum;
}

export const useUserServicesPaginated = ({
  userId,
  limit = SERVICES_PAGE_LIMIT,
  page = 0,
  type,
}: UseUserServicesPaginatedParams) => {
  return useQuery<UserServicesResponse, Error>({
    queryKey: [...GO_SERVICE_QUERY_KEYS.userServices(userId), { limit: limit, page: page, type: type }],
    queryFn: () => getUserServices(userId, limit, page * limit, type),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!userId,
  });
};
